import { DesignModel, DesignObject, RunModel, AutomationParameterModel, SampleModel } from "../model/run-model"
import { ApplicationsResponse } from "../model/run-design-defaults-model"
import * as API from "../api"
import { v4 } from "uuid"
import { ApiResponse } from "apisauce"
import * as jobService from "./job-service"
import { ServerBarcode, UiRun, UiCollection, UiBiosample } from "../model/ui-run-model"
import { parseXML } from "../utils/shared/xml/parse-xml"
import { PipelineTemplate, PipelineTemplateMap } from "../model/pipeline-template-models"
import { apiCache } from "./api-cache"
import { reportError } from "../../core-components/shared/Error/ErrorBoundary"
import { getMultiJobApplicationName } from "../services/job-service"
import { Job } from "../model/job-model"
import LOCAL_TEST_DEFAULTS from "../utils/run-design/LocalRunDesignDefaults.json"
import { RunDesign } from "../model/run-design-model"
import { getLabelNumber, getOldBarcode } from "../../silos/runs/utils/helpers"
import { Dataset } from "../model/dataset-model"
import { RunQcObject } from "../model/run-qc-model"
import { isRevioOrVega } from "../model/instrument-type-model"
import { cleanupUsername } from "../model/user-model"
import { DEMUX_WORKFLOW_ID, PRE_ANALYSIS_IDS } from "../model/pipeline-template-models"
import { SYMMETRIC_BARCODES_TASK_OPTION } from "../utils/shared/barcode-helpers"

const DEBUG_USE_LOCAL_TEST_DEFAULTS = false;

export const getDesign = (id: string | number): Promise<DesignModel> => {

    return API.aRun(id).then(
        async response => {
            const run: RunQcObject = response.data
            let model = await responseToDesignModel(run)
            try {
                const runDesignReponse = await API.getRunDesign(id.toString())
                if (runDesignReponse) {
                    addRunDesignData(runDesignReponse.data, model)
                }
            } catch {} //okay for a run to not have a run-design found in SL.

            return model
        },
        error => {
            if( !(error instanceof API.ResourceNotFoundError) ) {
                reportError(error.originalError || "Unknown Run Design Error")
            }
            throw error
        }
    )
}

export const addRunDesignData = (runDesign: RunDesign, model: DesignModel) => {
    model.run.samples.forEach((sample, index) => {
        sample.barcodeCSVName = runDesign.samples[index].barcodeCsvFileName
        sample.application = runDesign.samples[index].application
        if (runDesign.samples[index].libraryType) {
            sample.libraryType = runDesign.samples[index].libraryType
        }
        sample.analysisName = runDesign.samples[index].analysisName  // TODO: "AutoAnalysisName" or "analysisName" ???
                                                                    // inside autoAnalysisEnabled or not ??
        sample.pipelineId = runDesign.samples[index].pipelineId
        sample.entryPoints = runDesign.samples[index].entryPoints
        sample.taskOptions = runDesign.samples[index].taskOptions
        sample.presetId = runDesign.samples[index].presetId
        sample.consensusMode = runDesign.samples[index].consensusMode
        sample.barcodedSamples = runDesign.samples[index].barcodedSamples

        if (runDesign.samples[index].autoAnalysisEnabled) {
            sample.AutoAnalysisEnabled = runDesign.samples[index].autoAnalysisEnabled
            sample.AutoAnalysisName = runDesign.samples[index].AutoAnalysisName
            if (sample.application === "masSeq") {
                sample.adapter = runDesign.samples[index].adapter
                sample.cellType = runDesign.samples[index].cellType
                sample.scIsoSeq = runDesign.samples[index].scIsoSeq
                sample.readSegmentation = runDesign.samples[index].readSegmentation
            }
        }
        if (runDesign.samples[index].isBarcoded) {
            sample.isBarcoded = true
            sample.barcodeUuid = runDesign.samples[index].barcodeUuid
            sample.sameBarcodesBothEnds = runDesign.samples[index].symmetricBarcodes
            sample.minBarcodeScore = 0
            sample.demuxBarcodes = runDesign.samples[index].demuxMode
            sample.barcodeCSVName = runDesign.samples[index].barcodeCsvFileName
            runDesign.samples[index].barcodedSamples.forEach(barcodedSample => {
                sample.barcodeUuidMap[barcodedSample.dnaBarcodeName] = barcodedSample.uuid
                sample.barcodeSampleMap[barcodedSample.dnaBarcodeName] = barcodedSample.bioSampleName
                if (barcodedSample.sampleData) {
                    sample.assayPlateIdMap[barcodedSample.dnaBarcodeName] = barcodedSample.sampleData.assayPlateId
                    sample.assayPlateWellMap[barcodedSample.dnaBarcodeName] = barcodedSample.sampleData.assayPlateWell
                }
            })
        }
    })
}

export const saveRunDesign = (runDesign: RunDesign, update: Boolean): Promise<RunDesign> => {
    if (update) {
        return API.updateRunDesign(runDesign).then(response => {
            return API.getRunDesign(response.data.uniqueId).then(response => {
                return response.data
            })
        },
        error => {
            if(error.data && error.data.message) {
                reportError(error.data.message)
            }
            throw error
        })
    } else {
        return API.addRunDesign(runDesign).then(response => {
            return API.getRunDesign(response.data.uniqueId).then(response => {
                return response.data
            })
        },
        error => {
            if(error.data && error.data.message) {
                reportError(error.data.message)
            }
            throw error
        })
    }
}

export const getUiRun = (uuid: string): Promise<UiRun> => {
    return getDesign(uuid).then( design => designModelToUiRun(design))
}

export const deleteDesign = (runUuid: string): Promise<ApiResponse<void>> => {
    return API.deleteRunDesign(runUuid)
}


export const getDesignFromMultiJobId = (id: number): Promise<DesignModel> => {
    return API.nRunsMultiJobId(id).then (response => getDesign(response.data[0].uniqueId))
}

export const getRuns = (projectId: number): Promise<UiRun[]> => {
    return getDesigns(projectId).then(designs => designs.map(designModelToUiRun))
}

const designModelToUiRun = (design: DesignModel): UiRun => {
    return {
        name: design.name,
        totalCells: design.totalCells,
        multiJobId: design.multiJobId || null,
        uniqueId: design.uniqueId,
        runSummary: design.runSummary,
        createdAt: design.createdAt,
        createdBy: cleanupUsername(design.run.createdBy),
        reserved: design.reserved
    }
}

export const getDesigns = (projectId: number): Promise<DesignModel[]> => {
    return API.nRunsAsDesigns(projectId).then( (runs) => {
        let ps: Promise<DesignModel>[] = []
        runs.data.forEach(
            run => {
                ps.push(responseToDesignModel(run))
            }
        )
        return Promise.all(ps)
    })
}

export const getCollections = (runId, projectId): Promise<UiCollection[]> => {
    return getDesign(runId).then(design => {
        return jobService.getCollectionJobIds(design, projectId).then(jobIdDict => {
            let collections: UiCollection[] = []
            design.run.samples.forEach(
                sample => {
                    const matchesProject = (projectId && projectId > 0) ? projectId === sample.projectId : true
                    if (matchesProject) {
                        let collection:UiCollection  = {
                            collectionName: sample.sampleName,
                            wellName: sample.wellName,
                            collectionUniqueId: sample.subreadSetUuid,
                            ccsUniqueId: sample.ccsUuid,
                            runName: design.run.runName,
                            runUniqueId: design.run.uuid,
                            jobId: jobIdDict[sample.subreadSetUuid] || null,
                            multiJobId: sample.multiJobId || null,
                            createdAt: design.createdAt,
                            numChildren:
                                Object.keys(sample.barcodeSampleMap).length > 0 ? Object.keys(sample.barcodeSampleMap).length : null
                        }
                        collections.push(collection)
                    }
                }
            )
            return collections
        })
    })
}

export const getBioSamples = (runId, collectionId): Promise<UiBiosample[]> => {
    return getDesign(runId).then(design => {
        let collection: SampleModel
        design.run.samples.forEach(sample => {
            if (sample.subreadSetUuid === collectionId) {
                collection = sample
            }
        })
        return jobService.getMultiJobJobIds(collection.multiJobId).then(jobIdDict => {
            let ps: UiBiosample[] = []
            Object.keys(collection.barcodeUuidMap).forEach(b => {
                let bioSample: UiBiosample = {
                    barcodeName: b,
                    barcodeUniqueId: collection.barcodeUuidMap[b],
                    bioSampleName: collection.barcodeSampleMap[b],
                    collectionName: collection.sampleName,
                    collectionUniqueId: collection.subreadSetUuid,
                    collectionCcsUniqueId: collection.ccsUuid,
                    runName: design.run.runName,
                    runUniqueId: design.run.uuid,
                    jobId: jobIdDict[collection.barcodeUuidMap[b]] || null,
                    multiJobId: collection.multiJobId || null
                }
                ps.push(bioSample)
            })
            return ps
        })
    })
}

export const getBarcodePairs = async (
    barcodes: Partial<Dataset>,
    isSymmetric: boolean
): Promise<string[]> => {

    if (!barcodes || !barcodes.uuid) {
        return null
    }

    return API.getBarcodeAllowedPairs(barcodes.uuid).then(response => {
        const pairs = response.data
        if (isSymmetric !== undefined && pairs.length > 0) {
            if ((isSymmetric && pairs[0][0] !== pairs[0][1]) ||
                (!isSymmetric && pairs[0][0] === pairs[0][1])) {
                return []
            }
        }
        return pairs.map(pair => pair[0] + "--" + pair[1])
    }, () => {
        return []
    })
}

export const getIsSymmetric = (sampleBarcodes): boolean => {
    if (!sampleBarcodes || sampleBarcodes.length === 0) {
        return true
    } else {
        const pairs = sampleBarcodes.map(bcPair => bcPair.split("--"))
        return pairs.every(pair => pair[0] === pair[1])
    }
}

export const addDesignBarcodes = async (design): Promise < DesignModel > => {
    const runId = design.run.uuid
    for (let i = 0; i < design.run.samples.length; i++) {
        let sample = design.run.samples[i]
        if (sample.isBarcoded) {
            let collectionId = sample.subreadSetUuid
            let barcodes = makeBarcodeData(sample)
            await API.saveCollectionBarcodes( {runId, collectionId, barcodes} )

        }
    }
    return Promise.resolve(design)
}

const makeBarcodeData = (sample): ServerBarcode[] => {
    let barcodeData: ServerBarcode[] = []
    Object.keys(sample.barcodeSampleMap).forEach(bc => {
        let barcode: ServerBarcode = {
            dnaBarcodeName: bc,
            bioSampleName: sample.barcodeSampleMap[bc],
            uuid: sample.barcodeUuidMap[bc],
            sampleData: {
                assayPlateId: sample.assayPlateIdMap[bc],
                assayPlateWell: sample.assayPlateWellMap[bc]
              }
        }
        barcodeData.push(barcode)
    })
    return barcodeData
}

export const responseToDesignModel = async (response): Promise<DesignModel> => {
    if (response.dataModel) {
        response.run = parseXML(response.dataModel)
        let barcodeSets  = await apiCache.getDatasets("barcodes")
        let jobSettings = []
        if (response.hasOwnProperty("multiJobId")) {
            await processMultiJobsOld(response, jobSettings)
        } else {
            await processMultiJobsNew(response, jobSettings)
        }
        await addCollectionInfo(response)
        const barcodingData = await addBarcodingData(response, jobSettings, barcodeSets)
        return new DesignModel(barcodingData)
    }
    return Promise.resolve(new DesignModel(response))
}

export const getRunsSequencingKit = (plateBarcode: string) => {
    return API.nRunsSequencingKit(getOldBarcode(plateBarcode), getLabelNumber(plateBarcode))
}

export const getUsedWells = (response: DesignModel): Promise<any[]> => {
    if (isRevioOrVega(response.run.instrumentType)) {
        // Build a list of plate info with barcode and plate number
        // This allows us to track which plate number each barcode corresponds to
        let plateInfo = []
        if (response.run.plate1Barcode) {
            plateInfo.push({ barcode: response.run.plate1Barcode, plateNumber: 1 })
        }
        if (response.run.plate2Barcode && response.run.plate2Barcode !== response.run.plate1Barcode) {
            plateInfo.push({ barcode: response.run.plate2Barcode, plateNumber: 2 })
        }
        
        let promises = plateInfo.map(plate => {
            let plateAssignedWells: string[] = []
            // A physical plate is identified by its sequencing kit and label number, which are
            // stable across runs. Plate position (1 or 2) is not: the same physical plate may be
            // loaded as plate 1 in one run and plate 2 in another, so used wells must be matched
            // by plate identity and then re-mapped onto this run's plate position.
            const plateSequencingKit = getOldBarcode(plate.barcode)
            const plateLabelNumber = getLabelNumber(plate.barcode)
            return getRunsSequencingKit(plate.barcode).then(assignedWellRuns => {
                let assignedWellRunUUIDs = assignedWellRuns.data.map(run => {return run.uniqueId})
                // Filter out the current run to avoid including its own wells
                assignedWellRunUUIDs = assignedWellRunUUIDs.filter(id => id !== response.uniqueId)
                let assignedWellRunDetailsPromises = assignedWellRunUUIDs.length > 0 ?
                assignedWellRunUUIDs.map(uuid => API.getRunDesign(uuid)) : []
                return Promise.all(assignedWellRunDetailsPromises).then(assignedWellRunDetails => {
                    if (assignedWellRunDetails) {
                        assignedWellRunDetails.forEach(details => {
                            details.data.samples.forEach(assignedSample => {
                                if (assignedSample.sequencingKit === plateSequencingKit &&
                                    assignedSample.labelNumber === plateLabelNumber) {
                                    const well = plate.plateNumber + "_" + assignedSample.wellName
                                    // Only add if not already present to avoid duplicates
                                    if (!plateAssignedWells.includes(well)) {
                                        plateAssignedWells.push(well)
                                    }
                                }
                            })
                        })
                    }
                    return plateAssignedWells
                })
            })
        })
        return Promise.all(promises).then(wellArrays => {
            let allAssignedWells: string[] = []
            wellArrays.forEach(plateWells => {
                plateWells.forEach(well => {
                    // Deduplicate when combining results from multiple plates
                    if (!allAssignedWells.includes(well)) {
                        allAssignedWells.push(well)
                    }
                })
            })
            return allAssignedWells
        })
    }
    return Promise.all([])
}

export const addCollectionInfo = async (response) => {
    response.run.samples.forEach(async sample => {
        let collectionInfo = await API.getCollectionInfo(response.run.uuid, sample.subreadSetUuid)
        if (collectionInfo.data["compatibilityTags"]) {
            let compatibilityTags = JSON.parse(collectionInfo.data["compatibilityTags"])
            sample.AutoAnalysisEnabled = compatibilityTags["AutoAnalysisEnabled"]
            sample.AutoAnalysisName = compatibilityTags["AutoAnalysisName"]
        }
    })
}

export const processMultiJobsNew = async (response: DesignObject, jobSettings) => {
    let getMultiJobJobsList = []
    response.run.samples.forEach(sample => {
        let getMultiJobJobs = sample.multiJobId ?
            API.nMultiJobJobs("multi-analysis", sample.multiJobId).then(response => {
                return response.data
            }) : Promise.resolve([])
        getMultiJobJobsList.push(getMultiJobJobs)
    })
    let multiJobJobsList = await Promise.all(getMultiJobJobsList)
    response.run.samples.forEach(
        (sample, index) => {
            multiJobJobsList[index]?.forEach(job => {
                let settings = JSON.parse(job.jsonSettings || "")
                if (PRE_ANALYSIS_IDS.includes(settings.pipelineId)) {
                    settings.entryPoints.forEach(eP => {
                        if (((eP.entryId === "eid_subread" && eP.datasetId === sample.subreadSetUuid) ||
                            (eP.entryId === "eid_ccs" && eP.datasetId === sample.ccsUuid))
                            ) {
                            sample["preAnalysisType"] = job.name
                            jobSettings.push(settings)
                        }
                    })
                } else {
                    sample.hasAutoAnalyses = true
                }
            })
        })
}

export const processMultiJobsOld = async (response: DesignObject, jobSettings) => {
    const { data: multiJobJobs } = await API.nMultiJobJobs("multi-analysis", response.multiJobId)
    response.run.samples.forEach(
        sample => {
            multiJobJobs.forEach(job => {
                let settings = JSON.parse(job.jsonSettings || "")
                settings.entryPoints.forEach(eP => {
                    if ((eP.entryId === "eid_subread" && eP.datasetId === sample.subreadSetUuid) ||
                        (eP.entryId === "eid_ccs" && eP.datasetId === sample.ccsUuid)) {
                        sample["preAnalysisType"] = job.name
                    }
                })
            })
        })
    multiJobJobs.forEach(job => {
        jobSettings.push(JSON.parse(job.jsonSettings))
    })
}

const addBarcodingData = async (response, jobSettings?, barcodesets?): Promise<DesignModel> => {
    const uuidMultiJobMap = {}
    let promises: Promise<void>[] = []
    if (jobSettings) {
        jobSettings.forEach(job => {
            if (job.pipelineId == DEMUX_WORKFLOW_ID) {
                const uuidDict = {}
                job.entryPoints.forEach(ep => {
                    uuidDict[ep.entryId] = ep.datasetId
                })
                const optionsMap = {}
                job.taskOptions.forEach(o => {
                    optionsMap[o.id] = o.value
                })
                const primaryDatasetId = "eid_ccs"
                uuidMultiJobMap[uuidDict[primaryDatasetId]] = {
                    barcodeUuid: uuidDict["eid_barcode"],
                    sameBarcodesBothEnds: optionsMap[SYMMETRIC_BARCODES_TASK_OPTION]
                }
            }
        })
    }

    if (response.run && response.run.samples) {
        response.run.samples.forEach(sample => {
            const sampleId = sample.ccsUuid
            const multiJob = uuidMultiJobMap[sampleId]

            if (multiJob) {
                let barcodesPromise = API.nRunsBarcodes(response.run.uuid, sample.subreadSetUuid).then(
                    ({ ok: barcodesOk, data: barcodes }) => {
                        if (!barcodesOk) {
                            return
                        }
                        if (barcodes!.length > 0) {
                            sample.barcodeSampleMap = {}
                            sample.barcodeUuidMap = {}
                            sample.assayPlateIdMap = {}
                            sample.assayPlateWellMap = {}
                            barcodes!.forEach(barcode => {
                                sample.barcodeSampleMap[barcode.dnaBarcodeName] = barcode.bioSampleName
                                sample.barcodeUuidMap[barcode.dnaBarcodeName] = barcode.uuid
                                if (barcode.sampleData) {
                                    sample.assayPlateIdMap[barcode.dnaBarcodeName] = barcode.sampleData.assayPlateId
                                    sample.assayPlateWellMap[barcode.dnaBarcodeName] = barcode.sampleData.assayPlateWell
                                }
                            })
                        }
                    })
                promises.push(barcodesPromise)
                sample.isBarcoded = true
                sample.barcodeUuid = multiJob.barcodeUuid
                sample.sameBarcodesBothEnds = multiJob.sameBarcodesBothEnds
                sample.minBarcodeScore = 0
            }
            if (barcodesets) {
                for (let barcodeset of barcodesets) {
                    if (barcodeset.uuid === sample.barcodeUuid) {
                        sample.barcodeName = barcodeset.name
                    }
                }
            }
        })
    }

    await Promise.all(promises)

    return Promise.resolve(response)
}

const getPipelineTemplatesMap: () => Promise <PipelineTemplateMap> = () => {

    return apiCache.getPipelineTemplates().then(templates => {
        if (!templates) { return {} }
        return templates.reduce(
            (templatesObject: any, template: PipelineTemplate) => {
                let property = {}
                property[template.id] = template
                return Object.assign(templatesObject, property)
            },
            {}
        )
    })
}


export const getDefaults: () => Promise<ApplicationsResponse> = () => {
    if (DEBUG_USE_LOCAL_TEST_DEFAULTS) {
        return Promise.resolve(LOCAL_TEST_DEFAULTS)
    }
    try {
        return apiCache.getRunDesignDefaults()
    } catch {
        return Promise.resolve({
            applicationCategories: [],
            applications: {}
        })
    }
}

// Reconstruct a multi-job's subJobTypeId from its child jobs: the distinct child
// pipeline ids, sorted and comma-joined. A child job carries its pipeline id on its
// own subJobTypeId, falling back to its jsonSettings. The parent multi-job's own
// subJobTypeId is not a reliable source here — it is absent for multi-jobs whose
// analysis child is added after the multi-job is created.
const getSubJobTypeIdFromChildJobs = (childJobs: Job[]): string => {
    const childPipelineIds = childJobs.map(
        job => job.subJobTypeId || JSON.parse(job.jsonSettings || "{}").pipelineId
    )
    return Array.from(new Set(childPipelineIds.filter(Boolean))).sort().join(",")
}

export const getSampleTable: (design: DesignModel) => Promise<any> = (design) => {
    return getPipelineTemplatesMap().then(templateMap => {
        let ps: Promise<any>[] = []
        design.run.samples.forEach(
            sample => {
                if (sample.multiJobId) {
                    let promise = API.getMultiJobJobs(sample.multiJobId).then(response => {
                        const subJobTypeId = getSubJobTypeIdFromChildJobs(response.data)
                        sample["autoAnalysisType"] = getMultiJobApplicationName(subJobTypeId, templateMap)
                    })
                    ps.push(promise)
                }
            }
        )
        return Promise.all(ps).then(() => {return design})
    })
}
export function populateRunValues(run: any) {
    if (run.uuid === "") {
        run.uuid = v4()
    }
    run.samples.forEach(s => {
        if (s.subreadSetUuid === "") {
            s.subreadSetUuid = v4()
        }
        if (s.collectionMetadataUuid === "") {
            s.collectionMetadataUuid = v4()
        }
        if (s.enableCCS !== "None" && s.ccsUuid === "") {
            s.ccsUuid = v4()
        }
        if (s.isBarcoded) {
            Object.keys(s.barcodeSampleMap).forEach((barcode) => {
                s.barcodeUuidMap[barcode] = s.barcodeUuidMap[barcode] || v4()
            })
        }
    })
}

export function removeDuplicateAutomationParameters(run: RunModel) {
    for (let sample of run.samples) {
        if (sample.automationParameters && sample.automationParameters.length > 0) {
            let apMap = new Map()
            for (let ap of sample.automationParameters) {
                apMap.set(ap.name, ap)
            }
            let uniqueAPs: AutomationParameterModel[] = []
            // eslint-disable-next-line
            for (let [name, ap] of apMap.entries()) {
                uniqueAPs.push(ap)
            }
            sample.automationParameters = uniqueAPs
        }
    }
}
